import { useEffect, useState } from 'react'
import {
  Breadcrumb,
  Button,
  ButtonGroup,
  Col,
  type BadgeProps,
  Form,
  Row,
  Spinner,
  Badge,
  Card,
  Table,
} from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { GET_MY_NOTIFICATIONS, GET_UNREAD_NOTIFICATIONS_BY_TYPE, GET_NOTIFICATION_TYPES_WITH_DATA } from '../../gql/queries/notificationsQuery'
import { GET_UNREAD_NOTIFICATIONS_COUNT } from '../../gql/queries/notificationsQuery'
import {
  MARK_NOTIFICATION_AS_READ,
  DELETE_NOTIFICATION,
  MARK_NOTIFICATION_AS_UNREAD,
} from '../../gql/mutations/notificationMutations'
import {EDIT_POLICY} from '../../gql/mutations/policyMutation'
import {LAUNCH_CAMPAIGN} from '../../gql/mutations/campaigns'
import {
  IGetMyNotificationsResponse,
  IMarkNotificationAsReadResponse,
  IDeleteNotificationResponse,
  IMarkNotificationAsUnreadResponse,
  INotification,
  NotificationType,
  IGetUnreadNotificationsByTypeResponse,
  IGetNotificationTypesWithDataResponse,
} from '../../types/notifications'
import { PaginationSection } from '../shared/PaginationSection'
import { SIZE_PAGE_DEFAULT } from '../../helpers/consts'
import { PaginationUtils } from '../shared/PaginationUtils'
import { parseId } from '../../helpers'
import { NotificationsSidebar } from './NotificationsSidebar'

// Normaliza el actionUrl a un href navegable para que el navegador ofrezca
// "Abrir en nueva pestaña / ventana" en el menú contextual.
const buildNotificationHref = (actionUrl?: string): string | undefined => {
  if (!actionUrl) return undefined
  if (/^https?:\/\//i.test(actionUrl)) return actionUrl
  return actionUrl.startsWith('/') ? actionUrl : `/${actionUrl}`
}

// Click con modificador (cmd/ctrl/shift/alt) o botón central: dejamos que el
// navegador abra la nueva pestaña/ventana en vez de navegar dentro del SPA.
const isBrowserHandledClick = (e: React.MouseEvent) =>
  e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0

export const NotificationsPage = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'read'>('all')
  const [selectedType, setSelectedType] = useState<NotificationType | 'ALL'>('ALL')
  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set())
  const [sortField, setSortField] = useState<'title' | 'notification_type' | 'priority' | 'created' | 'read_at'>('created')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Fetch notifications with pagination
  const { data, loading, error, refetch } = useQuery<IGetMyNotificationsResponse>(
    GET_MY_NOTIFICATIONS,
    {
      variables: {
        first,
        offset: (currentPage - 1) * first,
        isRead: activeTab === 'all' ? null : activeTab === 'read',
        notificationType: selectedType === 'ALL' ? null : selectedType,
        search: searchTerm.trim() === '' ? null : searchTerm.trim(),
        orderBy: [
          `${sortDirection === 'asc' ? '' : '-'}${sortField}`,
          // keep created as secondary to stabilize ordering
          sortField === 'created' ? undefined : '-created',
        ].filter(Boolean),
      },
      fetchPolicy: 'network-only',
      pollInterval: 300000, // Poll every 5 minutes
    }
  )

  const notifications = data?.allNotifications?.edges?.map((edge) => edge.node) || []
  const totalNotifications = data?.allNotifications?.totalCount || 0

  // Fetch unread counts by type
  const { data: typeCountsData, loading: typeCountsLoading } =
    useQuery<IGetUnreadNotificationsByTypeResponse>(GET_UNREAD_NOTIFICATIONS_BY_TYPE, {
      fetchPolicy: 'network-only',
      pollInterval: 300000, // Poll every 5 minutes
    })

  // Fetch available notification types (that have data)
  const { data: availableTypesData, loading: availableTypesLoading } =
    useQuery<IGetNotificationTypesWithDataResponse>(GET_NOTIFICATION_TYPES_WITH_DATA, {
      fetchPolicy: 'network-only',
      pollInterval: 300000, // Poll every 5 minutes
    })

  const typeCounts = typeCountsData?.unreadNotificationsByType || []
  const availableTypes = availableTypesData?.notificationTypesWithData || []

  const refetchUnread = {query: GET_UNREAD_NOTIFICATIONS_COUNT}
  const refetchTypeCounts = {query: GET_UNREAD_NOTIFICATIONS_BY_TYPE}
  const refetchAvailableTypes = {query: GET_NOTIFICATION_TYPES_WITH_DATA}

  const [markAsRead] = useMutation<IMarkNotificationAsReadResponse>(MARK_NOTIFICATION_AS_READ, {
    refetchQueries: [refetchUnread, refetchTypeCounts, refetchAvailableTypes],
    awaitRefetchQueries: true,
  })
  const [markAsUnread] = useMutation<IMarkNotificationAsUnreadResponse>(
    MARK_NOTIFICATION_AS_UNREAD,
    {
      refetchQueries: [refetchUnread, refetchTypeCounts, refetchAvailableTypes],
      awaitRefetchQueries: true,
    }
  )
  const [deleteNotification] = useMutation<IDeleteNotificationResponse>(DELETE_NOTIFICATION, {
    refetchQueries: [refetchUnread, refetchTypeCounts, refetchAvailableTypes],
    awaitRefetchQueries: true,
  })
  const [editPolicy, {loading: approvingAssignment}] = useMutation(EDIT_POLICY)
  const [launchCampaign, {loading: approvingCampaignLaunch}] = useMutation(LAUNCH_CAMPAIGN)

  useEffect(() => {
    if (error) {
      toast.error(`Error loading notifications: ${error.message}`)
    }
  }, [error])

  useEffect(() => {
    refetch()
    setSelectedNotifications(new Set()) // Clear selection when filters change
  }, [activeTab, selectedType, currentPage, first, searchTerm, sortField, sortDirection, refetch])

  // Select all toggle
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = new Set(notifications.map((n) => n.id))
      setSelectedNotifications(allIds)
    } else {
      setSelectedNotifications(new Set())
    }
  }

  // Toggle individual notification
  const handleSelectNotification = (notificationId: string) => {
    const newSelected = new Set(selectedNotifications)
    if (newSelected.has(notificationId)) {
      newSelected.delete(notificationId)
    } else {
      newSelected.add(notificationId)
    }
    setSelectedNotifications(newSelected)
  }

  const handleMarkAsReadOnly = async (notification: INotification) => {
    // Mark as read without navigating
    if (!notification.isRead) {
      try {
        await markAsRead({
          variables: { notificationId: parseInt(String(parseId(notification.id))) },
        })
        refetch()
      } catch (err: any) {
        toast.error(`Error marking notification as read: ${err.message}`)
      }
    }
  }

  const handleNavigateToNotification = async (notification: INotification) => {
    // Mark as read and navigate
    if (!notification.isRead) {
      try {
        await markAsRead({
          variables: { notificationId: parseInt(String(parseId(notification.id))) },
        })
        refetch()
      } catch (err: any) {
        toast.error(`Error marking notification as read: ${err.message}`)
      }
    }

    // Navigate to action URL if exists
    if (notification.actionUrl) {
      navigate(notification.actionUrl)
    }
  }

  const handleApproveAssignment = async (notification: INotification) => {
    if (notification.notificationType !== NotificationType.POLICY_ASSIGNMENT_REQUEST) {
      toast.error('This notification is not a policy assignment request')
      return
    }

    // Normaliza metadata: algunos backends envían string JSON
    let metadata: any = notification.metadata || {}
    if (typeof metadata === 'string') {
      try {
        metadata = JSON.parse(metadata)
      } catch (err) {
        console.warn('Failed to parse metadata string', metadata)
        metadata = {}
      }
    }

    const rawPolicyId =
      (metadata as any).policy_id ??
      (metadata as any).policyId ??
      (metadata as any).policy ??
      (metadata as any).id
    const policyId = rawPolicyId ? parseInt(String(parseId(rawPolicyId))) : null
    const requesterId = notification.createdBy?.id ? parseInt(String(parseId(notification.createdBy.id))) : null

    // Debug logs
    console.log('Notification:', notification)
    console.log('Metadata:', metadata)
    console.log('Metadata keys:', Object.keys(metadata || {}))
    console.log('Policy ID raw:', rawPolicyId)
    console.log('Policy ID parsed:', policyId)
    console.log('Requester ID parsed:', requesterId)
    console.log('Created By:', notification.createdBy)

    if (!policyId || !requesterId) {
      toast.error(`Missing required data to approve assignment. PolicyId: ${policyId}, RequesterId: ${requesterId}`)
      return
    }

    try {
      const {data: mutationData} = await editPolicy({
        variables: {
          id: policyId,
          policyData: {
            assignedTo: requesterId,
          },
        },
      })

      if (mutationData?.updatePolicy?.success) {
        toast.success('Policy assignment approved successfully')

        // Mark notification as read
        await markAsRead({
          variables: { notificationId: parseInt(String(parseId(notification.id))) },
        })

        refetch()
      } else {
        const errorMessage = mutationData?.updatePolicy?.errors?.[0]?.message || 'Failed to approve assignment'
        toast.error(errorMessage)
      }
    } catch (err: any) {
      toast.error(`Error approving assignment: ${err.message}`)
    }
  }

  const handleApproveCampaignLaunch = async (notification: INotification) => {
    let metadata: any = notification.metadata || {}
    if (typeof metadata === 'string') {
      try { metadata = JSON.parse(metadata) } catch { metadata = {} }
    }

    const rawCampaignId = metadata?.campaign_id ?? metadata?.campaignId
    if (!rawCampaignId) {
      toast.error('Missing campaign data in notification')
      return
    }

    try {
      const { data: mutationData } = await launchCampaign({
        variables: { id: String(rawCampaignId), runAsync: true },
      })

      if (mutationData?.launchCampaign?.ok) {
        toast.success('Campaign approved and launched')
        await markAsRead({
          variables: { notificationId: parseInt(String(parseId(notification.id))) },
        })
        refetch()
      } else {
        toast.error('Failed to launch campaign')
      }
    } catch (err: any) {
      toast.error(`Error approving campaign launch: ${err.message}`)
    }
  }

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.size === 0) {
      toast.warning('Please select notifications first')
      return
    }

    try {
      const promises = Array.from(selectedNotifications).map((id) =>
        markAsRead({
          variables: { notificationId: parseInt(String(parseId(id))) },
        })
      )
      await Promise.all(promises)
      toast.success(`${selectedNotifications.size} notifications marked as read`)
      setSelectedNotifications(new Set())
      refetch()
    } catch (err: any) {
      toast.error(`Error marking notifications as read: ${err.message}`)
    }
  }

  const handleMarkSelectedAsUnread = async () => {
    if (selectedNotifications.size === 0) {
      toast.warning('Please select notifications first')
      return
    }

    try {
      const promises = Array.from(selectedNotifications).map((id) =>
        markAsUnread({
          variables: { notificationId: parseInt(String(parseId(id))) },
        })
      )
      await Promise.all(promises)
      toast.success(`${selectedNotifications.size} notifications marked as unread`)
      setSelectedNotifications(new Set())
      refetch()
    } catch (err: any) {
      toast.error(`Error marking notifications as unread: ${err.message}`)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedNotifications.size === 0) {
      toast.warning('Please select notifications first')
      return
    }

    if (!window.confirm(`Are you sure you want to delete ${selectedNotifications.size} notification(s)?`)) {
      return
    }

    try {
      const promises = Array.from(selectedNotifications).map((id) =>
        deleteNotification({
          variables: { notificationId: parseInt(String(parseId(id))) },
        })
      )
      await Promise.all(promises)
      toast.success(`${selectedNotifications.size} notifications deleted`)
      setSelectedNotifications(new Set())
      refetch()
    } catch (err: any) {
      toast.error(`Error deleting notifications: ${err.message}`)
    }
  }

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'POLICY_NOTE_MENTION':
        return 'bi-chat-left-text text-info'
      case 'POLICY_STATUS_CHANGE':
        return 'bi-arrow-left-right text-warning'
      case 'POLICY_ASSIGNMENT':
        return 'bi-person-check text-success'
      case 'POLICY_ASSIGNMENT_REQUEST':
        return 'bi-person-fill-exclamation text-warning'
      case 'POLICY_PAYMENT_VIEW':
        return 'bi-credit-card text-primary'
      case 'COMMISSION_CLAIM':
        return 'bi-cash-coin text-warning'
      case 'COMMISSION_APPROVED':
        return 'bi-check-circle text-success'
      case 'COMMISSION_REJECTED':
        return 'bi-x-circle text-danger'
      case 'POLICY_REMINDER':
        return 'bi-alarm text-info'
      case 'LEAD_ASSIGNMENT':
        return 'bi-person-plus text-primary'
      case 'TICKET_ASSIGNED':
        return 'bi-ticket-perforated text-primary'
      case 'TICKET_STATUS_CHANGE':
        return 'bi-arrow-repeat text-warning'
      case 'TICKET_COMMENT':
        return 'bi-chat-dots text-info'
      case 'SIGNATURE_COMPLETED':
        return 'bi-pen-fill text-success'
      case 'SIGNATURE_PENDING':
        return 'bi-pen text-warning'
      case 'CAMPAIGN_LAUNCH_REQUEST':
        return 'bi-megaphone-fill text-warning'
      case 'GENERAL':
      default:
        return 'bi-bell text-secondary'
    }
  }

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'URGENT':
        return <Badge bg="danger" text="white" className="px-2">Urgent</Badge>
      case 'HIGH':
        return <Badge bg="warning" text="dark" className="px-2">High</Badge>
      case 'NORMAL':
        return <Badge bg="info" text="white" className="px-2">Normal</Badge>
      case 'LOW':
        return <Badge bg="light" text="dark" className="px-2">Low</Badge>
      default:
        return null
    }
  }

  const getTypeLabel = (type: NotificationType) => {
    const labels: Record<NotificationType, string> = {
      POLICY_NOTE_MENTION: 'Policy Mention',
      POLICY_STATUS_CHANGE: 'Policy Status',
      POLICY_ASSIGNMENT: 'Policy Assignment',
      POLICY_ASSIGNMENT_REQUEST: 'Assignment Request',
      POLICY_PAYMENT_VIEW: 'Payment View',
      COMMISSION_CLAIM: 'Commission Claim',
      COMMISSION_APPROVED: 'Commission Approved',
      COMMISSION_REJECTED: 'Commission Rejected',
      POLICY_REMINDER: 'Reminder',
      LEAD_ASSIGNMENT: 'Lead Assignment',
      TICKET_ASSIGNED: 'Ticket Assigned',
      TICKET_STATUS_CHANGE: 'Ticket Status',
      TICKET_COMMENT: 'Ticket Comment',
      SIGNATURE_COMPLETED: 'Signature Completed',
      SIGNATURE_PENDING: 'Signature Pending',
      CAMPAIGN_LAUNCH_REQUEST: 'Campaign Approval',
      GENERAL: 'General',
    }
    return labels[type] || type
  }

  const getTypeBadgeProps = (type: NotificationType): { bg: BadgeProps['bg']; text?: BadgeProps['text'] } => {
    const badges: Record<NotificationType, { bg: BadgeProps['bg']; text?: BadgeProps['text'] }> = {
      POLICY_NOTE_MENTION: { bg: 'info', text: 'white' },
      POLICY_STATUS_CHANGE: { bg: 'warning', text: 'dark' },
      POLICY_ASSIGNMENT: { bg: 'success', text: 'white' },
      POLICY_ASSIGNMENT_REQUEST: { bg: 'warning', text: 'dark' },
      POLICY_PAYMENT_VIEW: { bg: 'primary', text: 'white' },
      COMMISSION_CLAIM: { bg: 'warning', text: 'dark' },
      COMMISSION_APPROVED: { bg: 'success', text: 'white' },
      COMMISSION_REJECTED: { bg: 'danger', text: 'white' },
      POLICY_REMINDER: { bg: 'info', text: 'dark' },
      LEAD_ASSIGNMENT: { bg: 'primary', text: 'white' },
      TICKET_ASSIGNED: { bg: 'primary', text: 'white' },
      TICKET_STATUS_CHANGE: { bg: 'warning', text: 'dark' },
      TICKET_COMMENT: { bg: 'info', text: 'white' },
      SIGNATURE_COMPLETED: { bg: 'success', text: 'white' },
      SIGNATURE_PENDING: { bg: 'secondary', text: 'white' },
      CAMPAIGN_LAUNCH_REQUEST: { bg: 'warning', text: 'dark' },
      GENERAL: { bg: 'light', text: 'dark' },
    }
    return badges[type] || { bg: 'light', text: 'dark' }
  }

  const totalPages = Math.ceil(totalNotifications / first)
  const { start, end } = PaginationUtils.getPaginationRange(currentPage, first, totalNotifications)

  const allSelected = notifications.length > 0 && selectedNotifications.size === notifications.length
  const someSelected = selectedNotifications.size > 0 && selectedNotifications.size < notifications.length

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
    setCurrentPage(1)
  }

  const renderSortIcon = (field: typeof sortField) => {
    if (sortField !== field) return <i className='bi bi-arrow-down-up ms-1 text-muted'></i>
    return sortDirection === 'asc' ? (
      <i className='bi bi-caret-up-fill ms-1'></i>
    ) : (
      <i className='bi bi-caret-down-fill ms-1'></i>
    )
  }

  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-5'>
        <Breadcrumb>
          <Breadcrumb.Item href='/'>Home</Breadcrumb.Item>
          <Breadcrumb.Item active>Notifications</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <Row>
        <Col lg={2} xl={2} className='mb-5 mb-lg-0'>
          <NotificationsSidebar
            selectedType={selectedType}
            setSelectedType={(type) => {
              setSelectedType(type)
              setCurrentPage(1)
            }}
            typeCounts={typeCounts}
            availableTypes={availableTypes}
            loading={typeCountsLoading || availableTypesLoading}
          />
        </Col>
        <Col lg={10} xl={10}>
          <Card>
            <Card.Header>
              <div className='d-flex justify-content-between align-items-center'>
                <h3 className='card-title'>Notifications</h3>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Filters Row */}
              <Row className='mb-3'>
                <Col md={6}>
                  <ButtonGroup className='w-100'>
                    <Button
                      variant={activeTab === 'all' ? 'primary' : 'outline-primary'}
                      onClick={() => {
                        setActiveTab('all')
                        setCurrentPage(1)
                      }}
                    >
                      All
                    </Button>
                    <Button
                      variant={activeTab === 'unread' ? 'primary' : 'outline-primary'}
                      onClick={() => {
                        setActiveTab('unread')
                        setCurrentPage(1)
                      }}
                    >
                      Unread
                    </Button>
                    <Button
                      variant={activeTab === 'read' ? 'primary' : 'outline-primary'}
                      onClick={() => {
                        setActiveTab('read')
                        setCurrentPage(1)
                      }}
                    >
                      Read
                    </Button>
                  </ButtonGroup>
                </Col>
                <Col md={6}>
                  <Form.Control
                    type='text'
                    placeholder='Search subject or message'
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setCurrentPage(1)
                    }}
                  />
                </Col>
              </Row>

              {/* Bulk Actions Toolbar */}
              {selectedNotifications.size > 0 && (
                <div className='d-flex gap-2 mb-3 p-2 bg-light rounded'>
                  <span className='align-self-center text-muted me-3'>
                    {selectedNotifications.size} selected
                  </span>
                  <Button variant='outline-primary' size='sm' onClick={handleMarkSelectedAsRead}>
                    <i className='bi bi-check me-1'></i>
                    Mark as Read
                  </Button>
                  <Button variant='outline-secondary' size='sm' onClick={handleMarkSelectedAsUnread}>
                    <i className='bi bi-envelope me-1'></i>
                    Mark as Unread
                  </Button>
                  <Button variant='outline-danger' size='sm' onClick={handleDeleteSelected}>
                    <i className='bi bi-trash me-1'></i>
                    Delete
                  </Button>
                </div>
              )}

              {/* Pagination Section */}
              <PaginationSection
                first={first}
                setFirst={setFirst}
                currentPage={currentPage}
                totalPages={totalPages}
                start={start}
                end={end}
                totalPolicies={totalNotifications}
                updatePage={setCurrentPage}
                PaginationUtils={PaginationUtils}
              />

              {/* Notifications Table */}
              {loading ? (
                <div className='text-center py-5'>
                  <Spinner animation='border' role='status'>
                    <span className='visually-hidden'>Loading...</span>
                  </Spinner>
                </div>
              ) : notifications.length === 0 ? (
                <div className='text-center py-5'>
                  <i className='bi bi-bell-slash fs-1 text-muted'></i>
                  <p className='text-muted mt-3'>No notifications found</p>
                </div>
              ) : (
                <div className='table-responsive'>
                  <Table hover className='align-middle'>
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}>
                          <Form.Check
                            type='checkbox'
                            checked={allSelected}
                            ref={(input) => {
                              if (input) {
                                input.indeterminate = someSelected
                              }
                            }}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th style={{ width: '50px' }}></th>
                        <th style={{ minWidth: '220px' }}>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none'
                            onClick={() => toggleSort('title')}
                          >
                            Subject {renderSortIcon('title')}
                          </button>
                        </th>
                        <th style={{ width: '150px' }}>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none'
                            onClick={() => toggleSort('notification_type')}
                          >
                            Type {renderSortIcon('notification_type')}
                          </button>
                        </th>
                        <th style={{ width: '110px' }}>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none'
                            onClick={() => toggleSort('priority')}
                          >
                            Priority {renderSortIcon('priority')}
                          </button>
                        </th>
                        <th style={{ width: '180px' }}>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none'
                            onClick={() => toggleSort('created')}
                          >
                            Date {renderSortIcon('created')}
                          </button>
                        </th>
                        <th style={{ width: '160px' }}>
                          <button
                            type='button'
                            className='btn btn-link p-0 text-decoration-none'
                            onClick={() => toggleSort('read_at')}
                          >
                            Read {renderSortIcon('read_at')}
                          </button>
                        </th>
                        <th style={{ width: '100px' }} className='text-center'>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notifications.map((notification) => (
                        <tr
                          key={notification.id}
                          className={!notification.isRead ? 'fw-bold' : ''}
                          style={{
                            backgroundColor: !notification.isRead ? '#ffffff' : '#f5f5f5',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleMarkAsReadOnly(notification)}
                          onDoubleClick={() => handleNavigateToNotification(notification)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type='checkbox'
                              checked={selectedNotifications.has(notification.id)}
                              onChange={() => handleSelectNotification(notification.id)}
                            />
                          </td>
                          <td>
                            <i className={`${getNotificationIcon(notification.notificationType)} fs-4`} />
                          </td>
                          <td>
                            <div>
                              <div className='d-flex align-items-center gap-2'>
                                {(() => {
                                  const href = buildNotificationHref(notification.actionUrl)
                                  if (!href) return <span>{notification.title}</span>
                                  return (
                                    <a
                                      href={href}
                                      className='text-gray-900 text-hover-primary text-decoration-none'
                                      title='Open details (right click to open in a new tab/window)'
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        if (isBrowserHandledClick(e)) {
                                          // El navegador abre nueva pestaña/ventana: solo marcamos leída
                                          handleMarkAsReadOnly(notification)
                                          return
                                        }
                                        e.preventDefault()
                                        handleNavigateToNotification(notification)
                                      }}
                                      onAuxClick={(e) => {
                                        // Click central: nueva pestaña nativa del navegador
                                        if (e.button === 1) {
                                          e.stopPropagation()
                                          handleMarkAsReadOnly(notification)
                                        }
                                      }}
                                    >
                                      {notification.title}
                                    </a>
                                  )
                                })()}
                                {!notification.isRead && (
                                  <Badge bg='danger' text='white' pill className='ms-1'>
                                    New
                                  </Badge>
                                )}
                              </div>
                              <small className='text-muted'>{notification.message}</small>
                            </div>
                          </td>
                          <td>
                            {(() => {
                              const { bg, text } = getTypeBadgeProps(notification.notificationType)
                              return (
                                <Badge bg={bg} text={text} className='px-2'>
                                  {getTypeLabel(notification.notificationType)}
                                </Badge>
                              )
                            })()}
                          </td>
                          <td>
                            {getPriorityBadge(notification.priority)}
                          </td>
                          <td>
                            <div className='text-nowrap'>
                              <div>{format(new Date(notification.created), 'MMM dd, yyyy')}</div>
                              <small className='text-muted'>
                                {format(new Date(notification.created), 'h:mm a')}
                              </small>
                            </div>
                          </td>
                          <td>
                            {notification.isRead && notification.readAt ? (
                              <div className='text-nowrap'>
                                <div>{format(new Date(notification.readAt), 'MMM dd, yyyy')}</div>
                                <small className='text-muted'>
                                  {format(new Date(notification.readAt), 'h:mm a')}
                                </small>
                              </div>
                            ) : (
                              <div className='text-muted'>—</div>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className='d-flex gap-1 justify-content-center'>
                              {/* Mark as Read/Unread Button */}
                              {!notification.isRead ? (
                                <Button
                                  variant='outline-primary'
                                  size='sm'
                                  className='btn-icon'
                                  title='Mark as read'
                                  onClick={() => handleMarkAsReadOnly(notification)}
                                >
                                  <i className='bi bi-eye'></i>
                                </Button>
                              ) : (
                                <Button
                                  variant='outline-secondary'
                                  size='sm'
                                  className='btn-icon'
                                  title='Mark as unread'
                                  onClick={async () => {
                                    try {
                                      await markAsUnread({
                                        variables: {
                                          notificationId: parseInt(String(parseId(notification.id))),
                                        },
                                      })
                                      toast.success('Notification marked as unread')
                                      refetch()
                                    } catch (err: any) {
                                      toast.error(`Error: ${err.message}`)
                                    }
                                  }}
                                >
                                  <i className='bi bi-eye-slash'></i>
                                </Button>
                              )}

                              {/* Approve Assignment Button - only for POLICY_ASSIGNMENT_REQUEST notifications */}
                              {notification.notificationType === NotificationType.POLICY_ASSIGNMENT_REQUEST && (
                                <Button
                                  variant='outline-info'
                                  size='sm'
                                  className='btn-icon'
                                  title='Approve assignment'
                                  onClick={() => handleApproveAssignment(notification)}
                                  disabled={approvingAssignment}
                                >
                                  <i className='bi bi-check-circle'></i>
                                </Button>
                              )}

                              {/* Approve Campaign Launch Button - only for CAMPAIGN_LAUNCH_REQUEST notifications */}
                              {notification.notificationType === NotificationType.CAMPAIGN_LAUNCH_REQUEST && (
                                <Button
                                  variant='outline-success'
                                  size='sm'
                                  className='btn-icon'
                                  title='Approve and launch campaign'
                                  onClick={() => handleApproveCampaignLaunch(notification)}
                                  disabled={approvingCampaignLaunch}
                                >
                                  {approvingCampaignLaunch
                                    ? <Spinner animation='border' size='sm' />
                                    : <i className='bi bi-rocket-takeoff'></i>
                                  }
                                </Button>
                              )}

                              {/* Navigate Button - opens details in a new tab */}
                              {buildNotificationHref(notification.actionUrl) && (
                                <Button
                                  as='a'
                                  href={buildNotificationHref(notification.actionUrl)}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  variant='outline-success'
                                  size='sm'
                                  className='btn-icon'
                                  title='Go to details (new tab)'
                                  onClick={() => handleMarkAsReadOnly(notification)}
                                >
                                  <i className='bi bi-box-arrow-up-right'></i>
                                </Button>
                              )}

                              {/* Delete Button */}
                              <Button
                                variant='outline-danger'
                                size='sm'
                                className='btn-icon'
                                title='Delete'
                                onClick={async () => {
                                  if (window.confirm('Are you sure you want to delete this notification?')) {
                                    try {
                                      await deleteNotification({
                                        variables: {
                                          notificationId: parseInt(String(parseId(notification.id))),
                                        },
                                      })
                                      toast.success('Notification deleted')
                                      refetch()
                                    } catch (err: any) {
                                      toast.error(`Error: ${err.message}`)
                                    }
                                  }
                                }}
                              >
                                <i className='bi bi-trash'></i>
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}
